import apiService from '../utils/apiInterceptor';
import config from '../config';

/**
 * Service for handling orders-related API calls
 */
const ordersService = {
  updateTaskNote: async (orderId, note) => apiService.put(`/orders/${orderId}/task-note`, { order_notes: note }),
  /**
   * Get all orders (original endpoint)
   * @returns {Promise<Object>} Promise resolving to order data
   */
  getAllOrders: async () => {
    return await apiService.get('/orders');
  },
    getTaskPosition : async (taskId) => {
  return await apiService.get(`/onfleet/position/${taskId}`).then(res => res.data);
},
  // FIXED: Enhanced getAllOrdersAdmin with proper pagination and view mode support
  getAllOrdersAdmin: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // FIXED: Add pagination parameters - ensure they're properly handled
      if (params.page && params.page > 0) {
        queryParams.append('page', params.page);
      } else {
        queryParams.append('page', '1'); // Default to page 1
      }
      
      if (params.limit && params.limit > 0) {
        queryParams.append('limit', params.limit);
      } else {
        queryParams.append('limit', '50'); // Default limit
      }
      
      // FIXED: Add view mode parameter for reports page
      if (params.view_mode) {
        queryParams.append('view_mode', params.view_mode);
      }
      
      // Add sorting parameters
      if (params.sort_field) queryParams.append('sort_field', params.sort_field);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
      
      // Add date parameters
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.property_date) queryParams.append('property_date', params.property_date);
      
      // Add status filters
      if (params.order_status) queryParams.append('order_status', params.order_status);
      if (params.payment_status !== undefined && params.payment_status !== null && params.payment_status !== '') {
        queryParams.append('payment_status', params.payment_status);
      }
      
      // Add regular search parameter
      if (params.search) queryParams.append('search', params.search);
      
      // Add category-specific search parameters
      if (params.search_property) queryParams.append('search_property', params.search_property);
      if (params.search_address) queryParams.append('search_address', params.search_address);
      if (params.search_postcode) queryParams.append('search_postcode', params.search_postcode);
      if (params.search_customer) queryParams.append('search_customer', params.search_customer);
      
      // Add other parameters
      if (params.calculate_metrics) queryParams.append('calculate_metrics', 'true');
      if (params.user_id) queryParams.append('user_id', params.user_id);
      
      // FIXED: Add filters for cleaning services and VAT
      if (params.exclude_cleaning_services) {
        queryParams.append('exclude_cleaning_services', 'true');
      }
      if (params.show_prices_without_vat) {
        queryParams.append('show_prices_without_vat', 'true');
      }
      
      // FIXED: Add grouping and display options
      if (params.group_by) queryParams.append('group_by', params.group_by);
      if (params.include_items) queryParams.append('include_items', 'true');
      if (params.nested_view) queryParams.append('nested_view', 'true');
      
      // Request URL with query parameters
      const url = `/admin/orders?${queryParams.toString()}`;
      
      const response = await apiService.get(url);
      
      // FIXED: Ensure response includes pagination metadata
      if (response && !response.pagination && response.data) {
        // If pagination info is missing, create it
        const totalItems = response.total || response.data.length || 0;
        const currentPage = parseInt(params.page) || 1;
        const itemsPerPage = parseInt(params.limit) || 50;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        
        response.pagination = {
          current_page: currentPage,
          per_page: itemsPerPage,
          total: totalItems,
          total_pages: totalPages,
          has_more: currentPage < totalPages
        };
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  /**
   * Get all orders with pagination and filtering (admin panel)
   * @param {Object} params - Filter and pagination parameters
   * @returns {Promise<Object>} Promise resolving to order data
   */
  getAdminOrders: async (params = {}) => {
    try {
      const response = await apiService.get('/order/list', { params });
      return response;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  /**
   * Place a new order
   * @param {Object} orderData - Order data
   * @returns {Promise<Object>} Promise resolving to created order
   */
  placeOrder: async (orderData) => {
    return await apiService.post('/place-order', orderData);
  },

  /**
   * Get all orders (new endpoint)
   * @returns {Promise<Object>} Promise resolving to order data
   */
  getAllOrdersNew: async () => {
    return await apiService.get('/ordesrnew');
  },

  /**
   * Get order by ID (original endpoint)
   * @param {number|string} id - Order ID
   * @returns {Promise<Object>} Promise resolving to order data
   */
  getOrderById: async (id) => {
    return await apiService.get(`/orders/${id}`);
  },
  
  getOrderByIdAdmin: async (id) => {
    return await apiService.get(`/admin/orders/${id}`);
  },

  /**
   * Get order by ID (admin endpoint)
   * @param {number|string} orderId - Order ID
   * @returns {Promise<Object>} Promise resolving to order data
   */
  getAdminOrderById: async (orderId) => {
    try {
      const response = await apiService.get(`/order/detail/${orderId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching order with ID ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new order
   * @param {Object} orderData - Order data
   * @returns {Promise<Object>} Promise resolving to created order
   */
  createOrder: async (orderData) => {
    return await apiService.post('/orders', orderData);
  },

  /**
   * Update an order
   * @param {number|string} id - Order ID
   * @param {Object} orderData - Updated order data
   * @returns {Promise<Object>} Promise resolving to updated order
   */
  updateOrder: async (id, orderData) => {
    return await apiService.put(`/orders/${id}`, orderData);
  },

  /**
   * Delete an order (original endpoint)
   * @param {number|string} id - Order ID
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  deleteOrder: async (id) => {
    return await apiService.delete(`/orders/${id}`);
  },

  /**
   * Delete an order (admin endpoint)
   * @param {number|string} orderId - Order ID to delete
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  deleteAdminOrder: async (orderId) => {
    try {
      const response = await apiService.get(`/order/delete/${orderId}`);
      return response;
    } catch (error) {
      console.error(`Error deleting order with ID ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Update order status
   * @param {number|string} orderId - Order ID
   * @param {string} status - New status value
   * @param {string} notes - Optional notes about the status change
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  updateOrderStatus: async (orderId, status, notes = '') => {
    try {
      const response = await apiService.post('/admin/change-order-status', {
        order_id: orderId,
        order_status: status,
        admin_notes: notes
      });
      return response;
    } catch (error) {
      console.error(`Error updating status for order with ID ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Update order payment status
   * @param {number|string} orderId - Order ID
   * @param {number} status - Payment status (0 = Unpaid, 1 = Paid, 2 = Refunded, 3 = Partial Refund)
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  updatePaymentStatus: async (orderId, status) => {
    try {
      const response = await apiService.post('/change-payment-status', {
        order_id: orderId,
        payment_status: status
      });
      return response;
    } catch (error) {
      console.error(`Error updating payment status for order with ID ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Export orders to CSV/Excel
   * @param {Object} filters - Optional filter parameters
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  exportOrders: async (filters = {}) => {
    try {
      const response = await apiService.get('/export/payment', { params: filters });
      return response;
    } catch (error) {
      console.error('Error exporting orders:', error);
      throw error;
    }
  },

  /**
   * Process refund for an order
   * @param {number|string} orderId - Order ID
   * @param {Object} refundData - Refund information (type, amount, etc.)
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  processRefund: async (orderId, refundData) => {
    try {
      const formData = new FormData();
      formData.append('order_id', orderId);
      
      Object.keys(refundData).forEach(key => {
        formData.append(key, refundData[key]);
      });
      
      const response = await apiService.post('/make-refund', formData);
      return response;
    } catch (error) {
      console.error(`Error processing refund for order with ID ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Get collection orders (orders with Collected status)
   * @param {Object} params - Filter and pagination parameters
   * @returns {Promise<Object>} Promise resolving to order data
   */
  getCollectionOrders: async (params = {}) => {
    try {
      const response = await apiService.get('/collected-orders', { params });
      return response;
    } catch (error) {
      console.error('Error fetching collection orders:', error);
      throw error;
    }
  },

  /**
   * Get out for use orders (orders with En-route or Completed status)
   * @param {Object} params - Filter and pagination parameters
   * @returns {Promise<Object>} Promise resolving to order data
   */
  getOutForUseOrders: async (params = {}) => {
    try {
      const response = await apiService.get('/out-for-use', { params });
      return response;
    } catch (error) {
      console.error('Error fetching out for use orders:', error);
      throw error;
    }
  },

  /**
   * Mark order as paid
   * @param {number|string} orderId - Order ID
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  markOrderAsPaid: async (orderId) => {
    try {
      const response = await apiService.post('/markPendingAsPaid', {
        order_id: orderId
      });
      return response;
    } catch (error) {
      console.error(`Error marking order with ID ${orderId} as paid:`, error);
      throw error;
    }
  },

  /**
   * Get order ETA information
   * @param {number|string} orderId - Order ID
   * @returns {Promise<Object>} Promise resolving to order ETA data
   */
  getOrderEtaInfo: async (orderId) => {
    try {
      const response = await apiService.get(`${config.API_URL}/orders/${orderId}/eta`);
      return response;
    } catch (error) {
      console.error(`Error fetching ETA info for order with ID ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Get order analytics data
   * @param {Object} params - Filter parameters (date range, etc.)
   * @returns {Promise<Object>} Promise resolving to analytics data
   */
  getOrderAnalytics: async (params = {}) => {
    try {
      const response = await apiService.get('/order-reporting', { params });
      return response;
    } catch (error) {
      console.error('Error fetching order analytics:', error);
      throw error;
    }
  },

  /**
   * Update the status of multiple orders at once
   * @param {Array} orderIds - Array of order IDs to update
   * @param {string} newStatus - The new status to set
   * @returns {Promise} - Promise with the API response
   */
  updateBulkOrderStatus: async (orderIds, newStatus) => {
    try {
      const response = await apiService.post(`${config.API_URL}/orders/bulk-update`, {
        order_ids: orderIds,
        status: newStatus
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update order items (add, modify, remove)
   * @param {number|string} orderId - Order ID
   * @param {Object} itemsData - Data containing items to update, remove, etc.
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  updateOrderItems: async (orderId, itemsData) => {
    try {
      return await apiService.post(`/admin/orders/${orderId}/items`, itemsData);
    } catch (error) {
      console.error(`Error updating items for order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Apply discount to an order
   * @param {number|string} orderId - Order ID
   * @param {Object} discountData - Discount information (amount, type, etc.)
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  applyOrderDiscount: async (orderId, discountData) => {
    try {
      return await apiService.post(`/admin/orders/${orderId}/discount`, discountData);
    } catch (error) {
      console.error(`Error applying discount to order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Remove discount from an order
   * @param {number|string} orderId - Order ID
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  removeOrderDiscount: async (orderId) => {
    try {
      return await apiService.delete(`/admin/orders/${orderId}/discount`);
    } catch (error) {
      console.error(`Error removing discount from order ${orderId}:`, error);
      throw error;
    }
  },

  syncOnfleetTask: async (orderId) => {
    const response = await apiService.get(`/orders/${orderId}/onfleet-sync`);
    return response.task;
  },

  fetchOrderLogsById: async (orderId) => {
    const response = await apiService.get(`/orders/${orderId}/logs`);
    return response;
  },

  // Add this method to update order dates/times
  updateOrderDateTime: async (orderId, dateTimeData) => {
    try {
      const formData = new FormData();
      formData.append('order_id', orderId);
      
      if (dateTimeData.date) {
        formData.append('property_date', dateTimeData.date);
      }
      
      if (dateTimeData.arrival_times && dateTimeData.arrival_times.length === 2) {
        dateTimeData.arrival_times.forEach(time => {
          formData.append('arrival_times[]', time);
        });
      }
      
      if (dateTimeData.completion_times && dateTimeData.completion_times.length === 2) {
        dateTimeData.completion_times.forEach(time => {
          formData.append('completion_times[]', time);
        });
      }
      
      return await apiService.post(`${config.API_URL}/orders/${orderId}/update-datetime`, formData);
    } catch (error) {
      throw error;
    }
  }
};

export default ordersService;
